import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Images, Play, Search, Download, Loader2, RotateCcw } from "lucide-react";

const TYPE_FILTERS = [{ v: "", l: "All" }, { v: "photo", l: "Photo" }, { v: "video", l: "Video" }];
const ORIENT_LABEL = { vertical: "Vertical", square: "Square", landscape: "Landscape" };
const FORMAT_LABELS = { "4x4": "4×4 Square", "2x6": "2×6 Bejmat", "2x2": "2×2 Square" };

function FilterSelect({ value, onChange, placeholder, options, testid }) {
  return (
    <Select value={value || "ALL"} onValueChange={(v) => onChange(v === "ALL" ? "" : v)}>
      <SelectTrigger className="h-10 w-full bg-white sm:w-40" data-testid={testid}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className="max-h-72 bg-white">
        <SelectItem value="ALL">{placeholder}</SelectItem>
        {options.map((o) => (
          <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export default function MediaLibrary() {
  const [data, setData] = useState(null);
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [type, setType] = useState("");
  const [color, setColor] = useState("");
  const [format, setFormat] = useState("");
  const [application, setApplication] = useState("");
  const [orientation, setOrientation] = useState("");
  const [active, setActive] = useState(null);
  const [loading, setLoading] = useState(true);
  const debounce = useRef(null);

  const load = useCallback(async (pageNum = 1, append = false) => {
    setLoading(true);
    try {
      const params = { page: pageNum, page_size: 24 };
      if (search.trim()) params.q = search.trim();
      if (type) params.media_type = type;
      if (color) params.color = color;
      if (format) params.format = format;
      if (application) params.application = application;
      if (orientation) params.orientation = orientation;
      const r = await api.get("/dealer/media", { params });
      setData(r.data);
      setItems((prev) => (append ? [...prev, ...r.data.items] : r.data.items));
      setPage(pageNum);
    } finally {
      setLoading(false);
    }
  }, [search, type, color, format, application, orientation]);

  useEffect(() => {
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => load(1), 250);
    return () => clearTimeout(debounce.current);
  }, [load]);

  const reset = () => {
    setSearch(""); setType(""); setColor(""); setFormat(""); setApplication(""); setOrientation("");
  };
  const hasFilters = search || type || color || format || application || orientation;

  const openAsset = async (a) => {
    // opening the detail/preview is the meaningful engagement signal (MEDIA_VIEW)
    try {
      const r = await api.get(`/dealer/media/${a.id}`);
      setActive(r.data);
    } catch { /* asset deactivated between list and open */ }
  };

  const facets = data?.facets || { colors: [], formats: [], applications: [], orientations: [] };

  return (
    <div data-testid="dealer-media-library">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-terracotta">Media Library</p>
      <h1 className="mt-1 font-display text-3xl font-medium text-charcoal sm:text-4xl">Inspiration, ready to use.</h1>
      <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
        Approved Zelligio photography, video and visual content for client presentations, social media,
        mood boards and project inspiration.
      </p>

      <div className="mt-6 space-y-3">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-warmgrey" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="media-search-input"
            placeholder="Search title, description, color, MZ code, application, tags…"
            className="h-11 bg-white pl-9 focus-visible:ring-terracotta" />
        </div>
        <div className="flex flex-wrap items-center gap-2" data-testid="media-type-filters">
          {TYPE_FILTERS.map((t) => (
            <button key={t.v} type="button" onClick={() => setType(t.v)} data-testid={`media-type-${t.v || "all"}`}
              className={`rounded-full px-4 py-1.5 text-xs font-semibold uppercase tracking-wider transition-colors ${
                type === t.v ? "bg-charcoal text-white" : "border border-border bg-white text-warmgrey hover:border-charcoal"}`}>
              {t.l}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <FilterSelect value={color} onChange={setColor} placeholder="All colors" testid="media-filter-color"
            options={facets.colors.map((c) => ({ value: c.code, label: c.label }))} />
          <FilterSelect value={format} onChange={setFormat} placeholder="All formats" testid="media-filter-format"
            options={facets.formats.map((f) => ({ value: f, label: FORMAT_LABELS[f] || f }))} />
          <FilterSelect value={application} onChange={setApplication} placeholder="All applications" testid="media-filter-application"
            options={facets.applications.map((a) => ({ value: a, label: a }))} />
          <FilterSelect value={orientation} onChange={setOrientation} placeholder="All orientations" testid="media-filter-orientation"
            options={facets.orientations.map((o) => ({ value: o, label: ORIENT_LABEL[o] || o }))} />
          {hasFilters && (
            <Button variant="ghost" size="sm" onClick={reset} data-testid="media-reset-filters"
              className="text-xs text-terracotta hover:text-terracotta-hover">
              <RotateCcw className="mr-1 h-3 w-3" /> Reset filters
            </Button>
          )}
        </div>
      </div>

      {loading && items.length === 0 ? (
        <div className="mt-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-terracotta" /></div>
      ) : items.length === 0 ? (
        <div className="mt-16 text-center" data-testid="media-empty">
          <Images className="mx-auto h-10 w-10 text-limestone" />
          <p className="mt-3 text-sm text-muted-foreground">
            {hasFilters ? "No media matches these filters." : "The Media Library is being prepared — check back soon."}
          </p>
        </div>
      ) : (
        <>
          <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4" data-testid="media-grid">
            {items.map((a) => (
              <button key={a.id} type="button" onClick={() => openAsset(a)} data-testid={`media-card-${a.id}`}
                className="group text-left">
                <div className="relative aspect-square overflow-hidden rounded-lg border border-border bg-sand">
                  {a.media_type === "photo" ? (
                    <img src={`/api/dealer/media/${a.id}/${a.has_thumb ? "thumb" : "file"}`} alt={a.title}
                      loading="lazy" data-testid={`media-thumb-${a.id}`}
                      className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.03]" />
                  ) : (
                    <div className="flex h-full w-full items-center justify-center bg-charcoal/90">
                      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-terracotta text-white transition-transform duration-300 group-hover:scale-110">
                        <Play className="h-5 w-5 fill-current" />
                      </span>
                    </div>
                  )}
                  {a.featured && (
                    <span className="badge-status badge-reserved absolute left-2 top-2">Featured</span>
                  )}
                  <span className="absolute right-2 top-2 rounded-sm bg-charcoal/70 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-white">
                    {a.media_type}
                  </span>
                </div>
                <p className="mt-2 truncate text-sm font-medium text-charcoal">{a.title}</p>
                <p className="truncate text-xs text-warmgrey">
                  {[a.color_labels.join(", "), a.formats.map((f) => FORMAT_LABELS[f] || f).join(" + "),
                    ORIENT_LABEL[a.orientation]].filter(Boolean).join(" · ")}
                </p>
              </button>
            ))}
          </div>
          {items.length < (data?.total || 0) && (
            <div className="mt-8 text-center">
              <Button variant="outline" onClick={() => load(page + 1, true)} disabled={loading} data-testid="media-load-more"
                className="border-terracotta text-terracotta hover:bg-[#BC6955]/5">
                {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Load more ({items.length} of {data.total})
              </Button>
            </div>
          )}
        </>
      )}

      <p className="mt-10 border-t border-border pt-4 text-[11px] text-muted-foreground">
        Media provided for authorized Zelligio showroom partner marketing and client presentation use.
      </p>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto border-border bg-white sm:max-w-3xl" data-testid="media-detail-dialog">
          {active && (
            <>
              {active.media_type === "video" ? (
                <video controls preload="auto" src={`/api/dealer/media/${active.id}/file`}
                  className="max-h-[55vh] w-full rounded-lg bg-charcoal" data-testid="media-video-player" />
              ) : (
                <img src={`/api/dealer/media/${active.id}/file`} alt={active.title}
                  className="max-h-[55vh] w-full rounded-lg bg-sand object-contain" data-testid="media-photo-preview" />
              )}
              <h2 className="mt-4 font-display text-2xl font-medium text-charcoal" data-testid="media-detail-title">{active.title}</h2>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {active.color_labels.map((c) => (
                  <span key={c} className="badge-status badge-neutral">{c}</span>
                ))}
                {active.formats.map((f) => (
                  <span key={f} className="badge-status badge-neutral">{FORMAT_LABELS[f] || f}</span>
                ))}
                {active.applications.map((ap) => (
                  <span key={ap} className="badge-status badge-reserved">{ap}</span>
                ))}
                {active.orientation && (
                  <span className="badge-status badge-neutral">{ORIENT_LABEL[active.orientation] || active.orientation}</span>
                )}
              </div>
              {active.description && <p className="mt-3 text-sm text-warmgrey">{active.description}</p>}
              <div className="mt-5 flex flex-wrap items-center gap-4">
                <a href={`/api/dealer/media/${active.id}/download`} data-testid="media-download-button"
                  className="inline-flex h-11 items-center gap-2 rounded-md bg-terracotta px-5 text-sm font-medium text-white transition-colors hover:bg-terracotta-hover">
                  <Download className="h-4 w-4" /> Download original
                </a>
                <span className="text-[11px] text-muted-foreground">
                  Media provided for authorized Zelligio showroom partner marketing and client presentation use.
                </span>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
